/**
 * StoryHubSubNav — Sub-nav for the Story Hub section.
 * Rendered by SectionSubNavRouter when path.includes('/stories').
 *
 * Mobile (<md): collapses to a native <select> dropdown — no horizontal scrolling.
 * Desktop (md+): full horizontal tab row.
 */

import { BookOpen, ChevronDown, Package } from 'lucide-react'
import { FC } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'

export const StoryHubSubNav: FC = () => {
    const navigate = useNavigate()
    const { runId } = useParams<{ runId: string }>()
    const location = useLocation()

    if (!runId) return null

    const base = `/dashboard/persuasion-lab/${runId}`
    const isHub = location.pathname.startsWith(`${base}/stories`)
    const isAssets = location.pathname.startsWith(`${base}/creative-context`)

    const items = [
        { id: 'story-hub',    label: 'Story Hub',    path: `${base}/stories`,           icon: BookOpen,  active: isHub    },
        { id: 'story-assets', label: 'Story Assets', path: `${base}/creative-context`,  icon: Package,   active: isAssets },
    ]

    const currentItem = items.find(i => i.active) ?? items[0]

    const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const item = items.find(i => i.id === e.target.value)
        if (item) navigate(item.path)
    }

    return (
        /* sticky top-[120px] = app outer header (64px) + CampaignPageHeader (~56px) */
        <div className="bg-white border-b border-gray-200 sticky top-[120px] z-20 shadow-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6">

                {/* Mobile: native select dropdown */}
                <div className="md:hidden py-2">
                    <div className="relative">
                        <select
                            value={currentItem.id}
                            onChange={handleSelectChange}
                            className="w-full appearance-none rounded-lg border border-gray-200 bg-gray-50 pl-3 pr-8 py-2 text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                        >
                            {items.map(item => (
                                <option key={item.id} value={item.id}>{item.label}</option>
                            ))}
                        </select>
                        <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    </div>
                </div>

                {/* Desktop: full horizontal tab row */}
                <nav className="hidden md:flex items-center gap-1 py-3">
                    {items.map((item) => (
                        <button
                            key={item.path}
                            type="button"
                            onClick={() => navigate(item.path)}
                            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                item.active
                                    ? 'bg-violet-100 text-violet-900 shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                            }`}
                        >
                            <item.icon className={`w-4 h-4 shrink-0 ${item.active ? 'text-violet-600' : ''}`} />
                            <span>{item.label}</span>
                        </button>
                    ))}
                </nav>

            </div>
        </div>
    )
}
